import { z } from 'zod';

const STORE = 'exports';
const schema = z.object({
  id: z.string().regex(/^[a-f0-9]{64}$/),
  format: z.enum(['png', 'pdf']),
  orientation: z.enum(['landscape', 'portrait']),
  blob: z.instanceof(Blob),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  expiresAt: z.number(),
});
export type LocalExport = z.infer<typeof schema>;

async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('drawonamap-prepared-exports', 1);
    let expired = false;
    const timer = setTimeout(() => { expired = true; reject(new Error('Export storage timed out')); }, 3_000);
    request.onblocked = () => { expired = true; clearTimeout(timer); reject(new Error('Export storage is blocked')); };
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: 'id' }).createIndex('expiresAt', 'expiresAt');
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      if (expired) request.result.close();
      else resolve(request.result);
    };
    request.onerror = () => { clearTimeout(timer); reject(request.error); };
  });
}

export async function saveLocalExport(record: LocalExport): Promise<boolean> {
  try {
    const db = await database();
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        store.put(record);
        const expired = store.index('expiresAt').openCursor(IDBKeyRange.upperBound(Date.now()));
        expired.onsuccess = () => { const cursor = expired.result; if (cursor) { cursor.delete(); cursor.continue(); } };
        const timer = setTimeout(() => transaction.abort(), 3_000);
        transaction.oncomplete = () => { clearTimeout(timer); resolve(); };
        transaction.onerror = transaction.onabort = () => { clearTimeout(timer); reject(transaction.error); };
      });
      return true;
    } finally { db.close(); }
  } catch { return false; }
}

export async function readLocalExport(id: string): Promise<LocalExport | null> {
  try {
    const db = await database();
    try {
      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE);
        const request = transaction.objectStore(STORE).get(id);
        const timer = setTimeout(() => { transaction.abort(); reject(new Error('Export read timed out')); }, 3_000);
        request.onsuccess = () => {
          clearTimeout(timer);
          const parsed = schema.safeParse(request.result);
          resolve(parsed.success && parsed.data.expiresAt > Date.now() ? parsed.data : null);
        };
        request.onerror = () => { clearTimeout(timer); reject(request.error); };
      });
    } finally { db.close(); }
  } catch { return null; }
}

export async function exportChecksum(blob: Blob): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}

export class LocalExportStorageError extends Error {
  constructor() {
    super("This browser could not save your export. Allow browser storage or try another browser before you pay.");
  }
}
